import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Men's room schematic only: slot labels 1–14 (left wall, top→bottom as 14→1), 15–22 (top wall, left→right).
 * Other labels are not shown on this diagram (use the table).
 */
const TILE = 40;
/** Left wall top→bottom: 14, 13, …, 1 */
const LEFT_SLOTS = Array.from({ length: 14 }, (_, i) => String(14 - i));
const TOP_SLOTS = Array.from({ length: 8 }, (_, i) => String(i + 15));

export interface MenRoomLockerRow {
  id: string;
  slotLabel: string;
  userId: string | null;
  externalOccupantLabel?: string | null;
  assignee?: {
    firstName: string;
    lastName: string;
    department?: string | null;
  } | null;
}

/** Returns canonical "1".."22" for layout lookup, or null if not part of men's room grid. */
export function normalizeMenRoomSlotKey(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  if (!/^0*\d+$/.test(s)) return null;
  const n = parseInt(s, 10);
  if (Number.isNaN(n) || n < 1 || n > 22) return null;
  return String(n);
}

function formatUser(u: NonNullable<MenRoomLockerRow['assignee']>) {
  return `${u.firstName} ${u.lastName}${u.department ? ` (${u.department})` : ''}`;
}

export interface LockersMenRoomLayoutProps {
  /** Map canonical label "1".."22" → locker row */
  lockersByLabel: Map<string, MenRoomLockerRow>;
  canManage: boolean;
  /** Existing row opens assign dialog; missing row + canManage → add dialog with label */
  onSelectSlot: (row: MenRoomLockerRow | null, canonicalLabel: string) => void;
}

const LockersMenRoomLayout: React.FC<LockersMenRoomLayoutProps> = ({
  lockersByLabel,
  canManage,
  onSelectSlot
}) => {
  const { t } = useTranslation();

  const renderTile = (canonicalLabel: string) => {
    const row = lockersByLabel.get(canonicalLabel) ?? null;
    const hasRecord = !!row;
    const occupied = !!(row && (row.userId || (row.externalOccupantLabel && row.externalOccupantLabel.trim())));

    let bg: string;
    let borderColor: string;
    if (!hasRecord) {
      bg = 'action.hover';
      borderColor = 'divider';
    } else if (occupied) {
      bg = 'warning.light';
      borderColor = 'warning.main';
    } else {
      bg = 'success.light';
      borderColor = 'success.main';
    }

    const titleParts = [`#${canonicalLabel}`];
    if (!hasRecord) {
      titleParts.push(
        t(
          'lockers.layoutNotConfigured',
          'Not in the system yet — click to create this locker and optionally assign someone'
        )
      );
    } else if (occupied) {
      if (row.assignee) titleParts.push(formatUser(row.assignee));
      else if (row.externalOccupantLabel?.trim()) titleParts.push(row.externalOccupantLabel.trim());
      else titleParts.push(t('lockers.free', 'Free'));
    } else {
      titleParts.push(t('lockers.free', 'Free'));
    }

    const clickable = hasRecord || canManage;
    const title = titleParts.join(' — ');

    return (
      <Tooltip title={title}>
        <Box
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={() => clickable && onSelectSlot(row, canonicalLabel)}
          onKeyDown={(e) => {
            if (!clickable) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectSlot(row, canonicalLabel);
            }
          }}
          sx={{
            width: TILE,
            height: TILE,
            boxSizing: 'border-box',
            bgcolor: bg,
            border: '2px solid',
            borderColor,
            borderRadius: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            fontWeight: 600,
            cursor: clickable ? 'pointer' : 'default',
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }
          }}
        >
          {canonicalLabel}
        </Box>
      </Tooltip>
    );
  };

  const cols = `repeat(9, ${TILE}px)`;
  const rows = `repeat(15, ${TILE}px)`;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        {t('lockers.roomLayoutTitle', 'Room layout (men)')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: cols,
            gridTemplateRows: rows,
            gap: 0.5,
            p: 1.5,
            border: '3px solid',
            borderColor: 'text.primary',
            borderRadius: 1,
            width: 'fit-content'
          }}
        >
          {/* Row 1: corner + top wall 15–22 */}
          <Box sx={{ gridColumn: 1, gridRow: 1, width: TILE, height: TILE }} />
          {TOP_SLOTS.map((label, i) => (
            <Box key={`top-${label}`} sx={{ gridColumn: i + 2, gridRow: 1, display: 'flex', justifyContent: 'center' }}>
              {renderTile(label)}
            </Box>
          ))}

          {/* Left wall 1–14 */}
          {LEFT_SLOTS.map((label, i) => (
            <Box
              key={`left-${label}`}
              sx={{ gridColumn: 1, gridRow: i + 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {renderTile(label)}
            </Box>
          ))}

          {/* Floor interior — keeps grid spacing only (no fill) */}
          <Box
            sx={{
              gridColumn: '2 / span 8',
              gridRow: '2 / span 14',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 14 * TILE,
              minWidth: 8 * TILE,
              pointerEvents: 'none'
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('lockers.roomFloor', 'Room')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                bgcolor: 'success.light',
                border: '2px solid',
                borderColor: 'success.main',
                borderRadius: 0.5
              }}
            />
            <Typography variant="caption">{t('lockers.legendFree', 'Free locker')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                bgcolor: 'warning.light',
                border: '2px solid',
                borderColor: 'warning.main',
                borderRadius: 0.5
              }}
            />
            <Typography variant="caption">{t('lockers.legendTaken', 'Taken locker')}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LockersMenRoomLayout;
